import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, X, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { initAnalytics, notifyConsentChanged } from '@/lib/analytics';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const CookieConsent = () => {
  const { t } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences({ ...preferences, ...savedPreferences, essential: true });
      } catch {
        // ignore malformed consent
      }
    }
    initAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = { essential: true, analytics: true, marketing: true, functional: true };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    notifyConsentChanged(allAccepted);
    initAnalytics();
    setShowBanner(false);
  };

  const handleDeclineAll = () => {
    const essentialOnly = { essential: true, analytics: false, marketing: false, functional: false };
    setPreferences(essentialOnly);
    localStorage.setItem('cookie-consent', JSON.stringify(essentialOnly));
    notifyConsentChanged(essentialOnly);
    setShowBanner(false);
  };

  const handleSaveCustomPreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    notifyConsentChanged(preferences);
    initAnalytics();
    setShowBanner(false);
    setShowCustomize(false);
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: key === 'essential' ? true : value,
    }));
  };

  const categories: Array<{ key: keyof CookiePreferences; disabled?: boolean }> = [
    { key: 'essential', disabled: true },
    { key: 'analytics' },
    { key: 'marketing' },
    { key: 'functional' },
  ];

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[100] p-4 shadow-lg transition-opacity duration-300',
        showBanner ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      )}
      hidden={!showBanner}
      role="region"
      aria-label={t('cookieConsent.regionLabel')}
      aria-live="polite"
    >
      <div className="container max-w-6xl mx-auto">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Cookie className="h-6 w-6 text-primary" />
                <div>
                  <CardTitle className="text-lg">{t('cookieConsent.title')}</CardTitle>
                  <CardDescription className="mt-1">{t('cookieConsent.description')}</CardDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowBanner(false)}
                className="h-10 w-10"
                aria-label={t('cookieConsent.closeAria')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleAcceptAll} className="flex-1">
                {t('cookieConsent.acceptAll')}
              </Button>
              <Button variant="outline" onClick={handleDeclineAll} className="flex-1">
                {t('cookieConsent.declineAll')}
              </Button>
              <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('cookieConsent.customize')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('cookieConsent.dialog.title')}</DialogTitle>
                    <DialogDescription>{t('cookieConsent.dialog.description')}</DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6">
                    <div className="space-y-4">
                      {categories.map(({ key, disabled }) => (
                        <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="space-y-1">
                            <Label className="font-medium">{t(`cookieConsent.categories.${key}.title`)}</Label>
                            <p className="text-sm text-muted-foreground">
                              {t(`cookieConsent.categories.${key}.description`)}
                            </p>
                          </div>
                          <Switch
                            checked={preferences[key]}
                            disabled={disabled}
                            onCheckedChange={(checked) => updatePreference(key, checked)}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Button onClick={handleSaveCustomPreferences} className="flex-1">
                        {t('cookieConsent.savePreferences')}
                      </Button>
                      <Button variant="outline" onClick={() => setShowCustomize(false)} className="flex-1">
                        {t('cookieConsent.cancel')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="mt-3 text-xs text-muted-foreground">
              {t('cookieConsent.footer.prefix')}{' '}
              <a href="/privacy-policy" className="text-primary hover:underline">
                {t('cookieConsent.footer.privacyLink')}
              </a>{' '}
              {t('cookieConsent.footer.and')}{' '}
              <a href="/cookies" className="text-primary hover:underline">
                {t('cookieConsent.footer.cookieLink')}
              </a>
              .
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookieConsent;
